/**
 * usePersistedState Hook
 * 
 * A custom hook that persists state to localStorage with:
 * - Lazy initializer pattern to prevent layout flashes
 * - Automatic sync to localStorage on state changes
 * - Cross-tab synchronization via storage event listener
 * - Type-safe with try-catch wrapper for SSR safety
 */
import { useState, useEffect, useCallback } from 'react';

/**
 * @param {string} key - localStorage key
 * @param {any} defaultValue - Default value if no stored value exists
 * @param {object} options - Optional configuration
 * @param {function} options.serialize - Custom serializer (default: JSON.stringify)
 * @param {function} options.deserialize - Custom deserializer (default: JSON.parse)
 * @param {boolean} options.syncTabs - Enable cross-tab sync (default: true)
 * @param {any} options.initialValue - Override initial value (for URL-based initialization)
 */
export function usePersistedState(key, defaultValue, options = {}) {
  const {
    serialize = JSON.stringify,
    deserialize = JSON.parse,
    syncTabs = true,
    initialValue = undefined
  } = options;

  // Lazy initializer - reads from localStorage during initial state setup
  // This prevents the flash of default value before localStorage value is loaded
  const [state, setState] = useState(() => {
    // If initialValue is provided (e.g., from URL), use it directly
    if (initialValue !== undefined && initialValue !== '') {
      return initialValue;
    }
    
    try {
      const storedValue = localStorage.getItem(key);
      if (storedValue !== null) {
        // Handle simple string values that aren't JSON
        try {
          return deserialize(storedValue);
        } catch {
          // If parsing fails, return the raw string (for simple string values)
          return storedValue;
        }
      }
      return defaultValue;
    } catch (error) {
      // localStorage not available (SSR or private browsing)
      console.warn(`usePersistedState: Could not read "${key}" from localStorage`, error);
      return defaultValue;
    }
  });

  // Sync state changes to localStorage
  useEffect(() => {
    try {
      if (state === null || state === undefined) {
        localStorage.removeItem(key);
      } else {
        // For simple strings, store directly without JSON.stringify
        const valueToStore = typeof state === 'string' ? state : serialize(state);
        localStorage.setItem(key, valueToStore);
      }
    } catch (error) {
      console.warn(`usePersistedState: Could not write "${key}" to localStorage`, error);
    }
  }, [key, state, serialize]);

  // Listen for storage events to sync across tabs
  useEffect(() => {
    if (!syncTabs) return;

    const handleStorageChange = (event) => {
      if (event.key === key && event.newValue !== null) {
        try {
          try {
            setState(deserialize(event.newValue));
          } catch {
            // If parsing fails, use raw string value
            setState(event.newValue);
          }
        } catch (error) {
          console.warn(`usePersistedState: Error syncing "${key}" from storage event`, error);
        }
      } else if (event.key === key && event.newValue === null) {
        // Key was removed
        setState(defaultValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, defaultValue, deserialize, syncTabs]);

  // Wrapped setState that handles localStorage updates
  const setPersistedState = useCallback((value) => {
    setState((prevState) => {
      const newValue = typeof value === 'function' ? value(prevState) : value;
      return newValue;
    });
  }, []);

  // Clear function to remove from localStorage and reset to default
  const clearPersistedState = useCallback(() => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`usePersistedState: Could not remove "${key}" from localStorage`, error);
    }
    setState(defaultValue);
  }, [key, defaultValue]);

  return [state, setPersistedState, clearPersistedState];
}

// ============== SPECIFIC PERSISTED STATE HOOKS ==============

// Storage keys (centralized for consistency)
export const STORAGE_KEYS = {
  ISLAND: 'yourbuysite_selected_island',
  CATEGORY: 'lastSelectedCategory',
  SUBCATEGORY: 'lastSelectedSubcategory',
  LANGUAGE: 'language',
  THEME: 'theme',
  CURRENCY: 'selectedCurrency'
};

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'nl', 'es'];
export const DEFAULT_LANGUAGE = 'en';

/**
 * Hook for Island selection persistence
 * @param {string} defaultIsland - Default island code (default: 'curacao')
 */
export function usePersistedIsland(defaultIsland = 'curacao') {
  return usePersistedState(STORAGE_KEYS.ISLAND, defaultIsland);
}

/**
 * Hook for Category selection persistence
 * Priority: URL params > localStorage > default
 * 
 * CRITICAL: State is initialized SYNCHRONOUSLY from URL/localStorage
 * The component is "born" with the correct value - no updates needed on mount
 */

// Synchronous URL parser - called during useState initialization
const getInitialCategoryFromURL = () => {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    if (url.pathname.includes('/marketplace')) {
      const category = url.searchParams.get('category');
      if (category) return category;
    }
  } catch (e) { /* ignore */ }
  return null;
};

const getInitialSubcategoryFromURL = () => {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    if (url.pathname.includes('/marketplace')) {
      const subcategory = url.searchParams.get('subcategory');
      if (subcategory) return subcategory;
    }
  } catch (e) { /* ignore */ }
  return null;
};

const getFromLocalStorage = (key) => {
  try {
    return localStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
};

export function usePersistedCategory() {
  // Initialize state synchronously - component is "born" with correct value
  // Priority: URL (if on marketplace) > localStorage > empty string
  const [category, setCategory] = useState(() => {
    return getInitialCategoryFromURL() ?? getFromLocalStorage(STORAGE_KEYS.CATEGORY);
  });
  
  const [subcategory, setSubcategory] = useState(() => {
    return getInitialSubcategoryFromURL() ?? getFromLocalStorage(STORAGE_KEYS.SUBCATEGORY);
  });
  
  // Sync to localStorage when values change
  useEffect(() => {
    try {
      if (category) {
        localStorage.setItem(STORAGE_KEYS.CATEGORY, category);
      } else {
        localStorage.removeItem(STORAGE_KEYS.CATEGORY);
      }
    } catch (e) { /* ignore */ }
  }, [category]);
  
  useEffect(() => {
    try {
      if (subcategory) {
        localStorage.setItem(STORAGE_KEYS.SUBCATEGORY, subcategory);
      } else {
        localStorage.removeItem(STORAGE_KEYS.SUBCATEGORY);
      }
    } catch (e) { /* ignore */ }
  }, [subcategory]);
  
  // Cross-tab sync
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === STORAGE_KEYS.CATEGORY && e.newValue !== null) {
        setCategory(e.newValue);
      }
      if (e.key === STORAGE_KEYS.SUBCATEGORY && e.newValue !== null) {
        setSubcategory(e.newValue);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  
  // Clear subcategory when category changes
  const setCategoryWithReset = useCallback((newCategory) => {
    setCategory(newCategory);
    if (!newCategory) {
      setSubcategory('');
    }
  }, []);
  
  const clearCategory = useCallback(() => {
    setCategory('');
    try { localStorage.removeItem(STORAGE_KEYS.CATEGORY); } catch (e) { /* ignore */ }
  }, []);
  
  const clearSubcategory = useCallback(() => {
    setSubcategory('');
    try { localStorage.removeItem(STORAGE_KEYS.SUBCATEGORY); } catch (e) { /* ignore */ }
  }, []);

  return {
    category,
    setCategory: setCategoryWithReset,
    clearCategory,
    subcategory,
    setSubcategory,
    clearSubcategory
  };
}

/**
 * Hook for Language preference persistence
 * Strictly supports only EN, NL, ES
 */
export function usePersistedLanguage() {
  const [language, setLanguageRaw, clearLanguage] = usePersistedState(
    STORAGE_KEYS.LANGUAGE, 
    DEFAULT_LANGUAGE
  );

  // Validate and set language (only allow supported languages)
  const setLanguage = useCallback((lang) => {
    const normalizedLang = lang?.toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(normalizedLang)) {
      setLanguageRaw(normalizedLang);
    } else {
      console.warn(`usePersistedLanguage: Unsupported language "${lang}". Supported: ${SUPPORTED_LANGUAGES.join(', ')}`);
    }
  }, [setLanguageRaw]);

  // Ensure current language is valid
  const validLanguage = SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;

  return [validLanguage, setLanguage, clearLanguage];
}

/**
 * Hook for Theme preference persistence
 */
export function usePersistedTheme(defaultTheme = 'system') {
  return usePersistedState(STORAGE_KEYS.THEME, defaultTheme);
}

/**
 * Hook for Currency preference persistence
 */
export function usePersistedCurrency(defaultCurrency = 'USD') {
  return usePersistedState(STORAGE_KEYS.CURRENCY, defaultCurrency);
}

export default usePersistedState;
